<template>
  <div class="inventory-and-moq">
    <div class="input-section-title">
      Inventory & MOQ
    </div>
    <div class="input-section-wrapper">
      <!-- Min Qty Per Style -->
      <div class="min-qty-section">
        <ValidationProvider
          v-slot="{ errors, dirty }"
          name="Min Qty Per Style"
          rules="numeric"
          tag="div"
          vid="minQtyPerStyle"
          :immediate="true"
        >
          <b-form-group
            label="Min Qty Per Style"
            label-class="input-label"
            class="input-form-group"
          >
            <b-form-input
              v-model="minQtyPerStyle"
              type="number"
              placeholder="Enter Min Qty Per Style"
              class="input-form-group"
              :class="{ 'is-invalid': dirty && errors.length }"
              @keypress="onlyAllowPositiveDecimal"
              @paste="blockInvalidPaste"
            />
            <div class="error-section">
              <small class="text-danger">{{ errors[0] }}</small>
            </div>
          </b-form-group>
        </ValidationProvider>
      </div>
      <ValidationProvider
        v-slot="{ errors, dirty }"
        name="Min Qty Per Size"
        rules="numeric"
        tag="div"
        vid="minQtyPerSize"
        :immediate="true"
      >
        <b-form-group
          label="Min Qty Per Size"
          label-class="input-label"
          class="input-form-group"
        >
          <b-form-input
            v-model="minQtyPerSize"
            type="number"
            placeholder="Enter Min Qty Per Size"
            class="input-form-group"
            :class="{ 'is-invalid': dirty && errors.length }"
            @keypress="onlyAllowPositiveDecimal"
            @paste="blockInvalidPaste"
          />
          <div class="error-section">
            <small class="text-danger">{{ errors[0] }}</small>
          </div>
        </b-form-group>
      </ValidationProvider>

      <!-- Checkboxes -->
      <div class="checkboxes-row d-flex align-items-center">
        <b-form-checkbox
          v-model="isCarton"
          class="mr-3 custom-checkbox"
        >
          It's a pack
        </b-form-checkbox>
      </div>
    </div>

    <!-- Variant sections -->
    <div>
      <!-- Single size and color option -->
      <div
        v-if="isSingleColorSingleSize"
        class="sizes-grid very-single"
      >
        <ColorPicker
          :value="{ color: singleItem.color, colorFamily: singleItem.colorFamily, hexCode: singleItem.hexCode }"
          :item-index="0"
        />
        <div class="row-wrapper">
          <div class="input-wrapper">
            <div class="row-label-size row-label-margin">
              Size
            </div>
            <div>
              <b-form-group class="input-form-group">
                <b-form-input
                  :value="singleItem.size"
                  disabled
                  class="size-input text-left"
                />
              </b-form-group>
            </div>
          </div>

          <div class="input-wrapper">
            <div class="row-label-inventory row-label-margin">
              Inventory
            </div>
            <div>
              <b-form-group class="input-form-group">
                <b-form-input
                  v-model="singleItem.quantityAvailable"
                  disabled
                  type="number"
                  min="0"
                  class="size-input-single-item"
                />
              </b-form-group>
            </div>
          </div>
        </div>

        <div class="row-wrapper">
          <div
            v-if="isCarton"
            class="input-wrapper width-50"
          >
            <div class="row-label-pack-qty row-label-margin">
              Pack Qty *
            </div>
            <div>
              <ValidationProvider
                v-slot="{ errors, dirty }"
                name="Pack Qty"
                rules="required|numeric"
                tag="div"
                vid="singleItemPackQty"
                :immediate="true"
              >
                <b-form-group class="input-form-group">
                  <b-form-input
                    v-model="singleItem.value"
                    type="number"
                    min="0"
                    class="size-input text-left"
                    placeholder="Enter Pack Qty"
                    :class="{ 'is-invalid': dirty && errors.length }"
                    @input="(newVal) => handleSingleSizeChange({ value: newVal })"
                  />
                  <div class="error-section">
                    <small class="text-danger">{{ errors[0] }}</small>
                  </div>
                </b-form-group>
              </ValidationProvider>
            </div>
          </div>
          <div
            v-else
            class="input-wrapper"
          />
        </div>
      </div>

      <div v-else>
        <div
          v-for="(variant, variantIndex) in variants"
          :key="`variant-${variant.color}-${variantIndex}`"
          class="variant-section"
          :class="{'has-border': variants.length > 1}"
        >
          <div
            v-if="variants.length > 1"
            class="variant-header mb-2"
          >
            Variant {{ variantIndex + 1 }}
          </div>

          <!-- Color selectors -->
          <ColorPicker
            :value="{ color: variant.color, colorFamily: variant.colorFamily, hexCode: variant.hexCode }"
            @input="updated => {
              handleVariantChange(updated, variantIndex)
            }"
          />

          <!-- Sizes grid -->
          <div class="sizes-grid">
            <div
              v-if="!isSingleColorSingleSize"
              class="row-label-column"
            >
              <span class="row-label-size">
                Size
              </span>
              <span class="row-label-inventory">
                Inventory
              </span>
              <span
                v-if="isCarton"
                class="row-label-pack-qty"
              >
                Pack Qty *
              </span>
            </div>
            <div class="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th
                      v-for="(size, sizeIndex) in sizeOptions"
                      :key="`size-header-${sizeIndex}`"
                      class="size-cell"
                    >
                      {{ size }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="row in rowTypes">
                    <tr :key="row.key">
                      <td
                        v-for="(size, sizeIndex) in sizeOptions"
                        :key="`${row.key}-${sizeIndex}`"
                        class="size-cell"
                      >
                        <template v-if="row.validation">
                          <ValidationProvider
                            v-slot="{ errors, dirty, validate }"
                            :ref="`${variantIndex}-${row.key}-${size}`"
                            :name="`${row.label} - ${size}`"
                            :rules="row.rules"
                            :vid="`${variantIndex}-${row.key}-${size}`"
                            tag="div"
                            :immediate="true"
                          >
                            <b-form-input
                              v-model="variant[row.key][size]"
                              :type="row.type || 'number'"
                              :disabled="row.disabled"
                              min="0"
                              class="size-input"
                              :class="{ 'is-invalid': dirty && errors.length }"
                              @input="(newVal) => {
                                handleVariantChange({
                                  color: variant.color,
                                  colorFamily: variant.colorFamily,
                                  newVal,
                                  rowKey: row.key,
                                  size,
                                }, variantIndex, validate);
                              }"
                            />
                          </ValidationProvider>

                        </template>
                        <b-form-input
                          v-else
                          v-model="variant[row.key][size]"
                          :type="row.type || 'number'"
                          :disabled="row.disabled"
                          min="0"
                          class="size-input"
                          @input="(newVal) => handleVariantChange({ color: variant.color, colorFamily: variant.colorFamily, newVal, rowKey: row.key, size: size }, variantIndex)"
                        />
                      </td>
                    </tr>
                    <!-- Row-level error -->
                    <tr
                      v-if="rowErrors[`${variantIndex}-${row.key}`]"
                      :key="`${row.key}-error-row`"
                    >
                      <template v-if="row.validation">
                        <td :colspan="sizeOptions.length">
                          <div class="error-section row-errors">
                            <small class="text-danger">
                              {{ rowErrors[`${variantIndex}-${row.key}`] }}
                            </small>
                          </div>
                        </td>
                      </template>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormInput,
  BFormCheckbox,
} from 'bootstrap-vue'
import ColorPicker from '@/views/apps/product/edit/inventory-and-moq/ColorPicker.vue'
import { ValidationProvider } from 'vee-validate'
import { useInventory } from '@/views/apps/product/edit/input-sections/hooks/useInventory'
import { blockInvalidPaste, onlyAllowPositiveDecimal } from '@/views/apps/product/edit/utils'

export default {
  name: 'ProductInventory',
  components: {
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    ColorPicker,
    ValidationProvider,
  },
  data() {
    return {
      sizeOptions: [],
      variants: [],
      localProduct: {},
      variantBuildCount: 0,
      rowErrors: {},
    }
  },
  setup() {
    const {
      isCarton,
      items,
      minQtyPerSize,
      minQtyPerStyle,
    } = useInventory()
    return {
      isCarton,
      items,
      minQtyPerSize,
      minQtyPerStyle,
    }
  },
  computed: {
    isSingleColorSingleSize() {
      return this.items && this.items.length === 1
    },
    singleItem() {
      if (!this.items || this.items.length === 0) return {}

      const item = this.items[0]
      return {
        ...item,
        minQtyPerSize: item.minQtyPerSize ?? this.minQtyPerSize ?? 0,
        value: item.value ?? 0,
        quantityAvailable: item.quantityAvailable ?? 0,
      }
    },
    rowTypes() {
      return [
        {
          label: 'Inventory', key: 'inventory', condition: true, disabled: true, validation: false,
        },
        {
          label: 'Pack Qty *',
          key: 'packQty',
          condition: this.isCarton,
          validation: this.isCarton,
          rules: this.isCarton ? 'required|numeric' : 'numeric',
          type: 'number',
        },
      ].filter(row => row.condition)
    },
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.buildVariants(newItems)
      },
    },
  },
  methods: {
    blockInvalidPaste,
    onlyAllowPositiveDecimal,
    onValidationInput(errors, variantIndex, rowKey) {
      const key = `${variantIndex}-${rowKey}`

      if (errors.length > 0) {
        // Set a generic message for Pack Qty row
        this.$set(this.rowErrors, key, rowKey === 'packQty' ? 'The Pack Qty * fields are required' : errors[0])
      } else {
        // Remove the row error only if no other fields in the row have errors
        const otherErrorsExist = Object.keys(this.$refs)
          .filter(refKey => refKey.startsWith(`${variantIndex}-${rowKey}`))
          .some(refKey => {
            const provider = this.$refs[refKey]
            if (Array.isArray(provider)) {
              return provider.some(p => p.flags.invalid)
            }
            return provider && provider.flags && provider.flags.invalid
          })

        if (!otherErrorsExist) {
          this.$delete(this.rowErrors, key)
        }
      }
    },
    handleSingleSizeChange(newValues) {
      // Only update if the value property is explicitly present
      if ('value' in newValues) {
        const value = newValues.value ? Number(newValues.value) : newValues.value
        this.items = [
          {
            ...this.items[0],
            value,
          },
        ]
        this.buildSingleItem()
      }
    },
    handleVariantChange({
      rowKey, newVal, size, color, colorFamily, hexCode,
    }, variantIndex, validationRef) {
      if (validationRef) {
        validationRef().then(result => {
          this.onValidationInput(result.errors, variantIndex, rowKey)
        })
      }
      this.variants = this.variants.map((variant, index) => {
        if (index !== variantIndex) return variant
        return {
          ...variant,
          [rowKey]: {
            ...variant[rowKey],
            ...(size && newVal !== undefined ? { [size]: newVal } : {}),
          },
          color,
          colorFamily,
          ...(hexCode !== undefined ? { hexCode } : {}),
        }
      })
      this.buildItems(this.variants)
    },
    getGroupedItems(items) {
      if (!items) return {}
      const groups = {}
      const sizeOptions = []
      items.forEach(item => {
        if (!sizeOptions.includes(item.size)) sizeOptions.push(item.size)
        const key = `${item.colorFamily || ''}-${item.color || ''}`
        if (!groups[key]) {
          groups[key] = {
            colorFamily: item.colorFamily,
            color: item.color,
            hexCode: item.hexCode,
            items: {},
          }
        }
        groups[key].items[item.size] = {
          inventory: item.quantityAvailable,
          packQty: item.value,
          minQtyPerSize: item.minQtyPerSize,
          sku: item.sku,
        }
      })
      this.sizeOptions = sizeOptions
      return groups
    },
    buildSingleItem() {
      const newFields = {
        color: this.singleItem.color,
        colorFamily: this.singleItem.colorFamily,
        value: this.singleItem.value,
      }

      this.items = [
        {
          ...this.items[0],
          ...newFields,
        },
      ]
    },
    buildItems(variants) {
      const newItems = []
      this.items.forEach(item => {
        const variant = variants.find(v => v.skuIds.includes(item.sku))
        if (!variant) return

        const value = variant.packQty[item.size]
        const colorFamily = variant.colorFamily
        const color = variant.color
        const hexCode = variant.hexCode

        newItems.push({
          ...item,
          value,
          colorFamily,
          color,
          hexCode,
        })
      })
      this.items = newItems
    },
    buildVariants(items) {
      const groupedItems = this.getGroupedItems(items)
      if (Object.keys(groupedItems).length === 0) return
      this.variants = Object.values(groupedItems).map(variant => {
        const inventoryObj = {}
        const packQtyObj = {}
        const minQtyPerSizeObj = {}
        const skuIds = []

        this.sizeOptions.forEach(size => {
          const sizeData = variant.items[size] || {}
          inventoryObj[size] = sizeData.inventory || 0
          packQtyObj[size] = sizeData.packQty ?? null
          minQtyPerSizeObj[size] = sizeData.minQtyPerSize || null
          skuIds.push(sizeData.sku)
        })
        return {
          colorFamily: variant.colorFamily,
          color: variant.color,
          hexCode: variant.hexCode,
          inventory: inventoryObj,
          packQty: packQtyObj,
          minQtyPerSize: minQtyPerSizeObj,
          skuIds,
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.inventory-and-moq {
  background-color: white;
  border-radius: 8px;
  padding: 16px 0;
  padding-top: 40px;

  .row-errors {
    width: max-content;
  }

  .input-section-wrapper {
    display: flex;
    gap: 32px;
    align-items: baseline;
    margin-bottom: 24px;
    .checkboxes-row {
      gap: 24px;
    }

    // Override BootstrapVue checkbox color
    ::v-deep {
      .custom-control-input:checked ~ .custom-control-label::before {
        background-color: #15A1CC !important;
        border-color: #15A1CC !important;
      }

      // Remove box-shadow on focus/hover
      .custom-control-input:focus ~ .custom-control-label::before {
        box-shadow: none !important;
      }

      // Remove box-shadow when checked
      .custom-control-input:checked ~ .custom-control-label::before {
        box-shadow: none !important;
      }
    }
  }
}
</style>
